/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { orderService } from 'services/order.service'

import { _object } from 'utils/interfaces'
import { dateFormatWitTime, formatPaymentType, formatPrice } from 'utils'

import { Placeholder } from 'assets/images'

const OrderDetails = () => {
	const { id } = useParams()
	const [order, setOrder] = useState<_object>({})
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		orderService.getOrdersDetails(id).then((results) => {
			setOrder(results)
		})
	}, [id])

	const downloadInvoiced = async () => {
		setLoading(true)
		const results = await orderService.getOrdersInvoiced(id)
		const pdfUrl = results?.pdfFile
		if (pdfUrl) {
			setLoading(false)
			window.open(pdfUrl, '_blank')
		}
	}

	return (
		<>
			<div className="mb-6 flex flex-row items-start justify-between">
				<div>
					{order?.order_number && <h1 className="mb-1 text-2xl font-semibold">
						Order #{order?.order_number}{' '}
						<small className="block text-sm ">{dateFormatWitTime(order?.created_at)}</small>
					</h1>}
					{order?.status && <small className="block text-sm">
						Order status: {order?.status?.charAt(0)?.toUpperCase() + order?.status?.slice(1)}
					</small>
					}
				</div>
				<div>
					<button
						type="button"
						className="relative bg-green-100 w-auto text-green-400 text-sm flex items-center justify-center mb-2 font-medium px-3 py-1 rounded-md border border-green-400 disabled:opacity-50"
						onClick={() => downloadInvoiced()}
						disabled={loading}
					>

						{/* Text always visible */}
						<svg
							xmlns="http://www.w3.org/2000/svg"
							version="1.1"
							width="20"
							height="20"
							x="0"
							y="0"
							viewBox="0 0 512 512"
						>
							<g>
								<path
									d="M386.689 304.403c-35.587 0-64.538 28.951-64.538 64.538s28.951 64.538 64.538 64.538c35.593 0 64.538-28.951 64.538-64.538s-28.951-64.538-64.538-64.538zm0 96.807c-17.796 0-32.269-14.473-32.269-32.269s14.473-32.269 32.269-32.269 32.269 14.473 32.269 32.269c0 17.797-14.473 32.269-32.269 32.269zM166.185 304.403c-35.587 0-64.538 28.951-64.538 64.538s28.951 64.538 64.538 64.538 64.538-28.951 64.538-64.538-28.951-64.538-64.538-64.538zm0 96.807c-17.796 0-32.269-14.473-32.269-32.269s14.473-32.269 32.269-32.269c17.791 0 32.269 14.473 32.269 32.269 0 17.797-14.473 32.269-32.269 32.269zM430.15 119.675a16.143 16.143 0 0 0-14.419-8.885h-84.975v32.269h75.025l43.934 87.384 28.838-14.5-48.403-96.268z"
									fill="#31c48d"
									opacity="1"
									data-original="#000000"
								></path>
								<path
									d="M216.202 353.345h122.084v32.269H216.202zM117.781 353.345H61.849c-8.912 0-16.134 7.223-16.134 16.134 0 8.912 7.223 16.134 16.134 16.134h55.933c8.912 0 16.134-7.223 16.134-16.134 0-8.912-7.223-16.134-16.135-16.134zM508.612 254.709l-31.736-40.874a16.112 16.112 0 0 0-12.741-6.239H346.891V94.655c0-8.912-7.223-16.134-16.134-16.134H61.849c-8.912 0-16.134 7.223-16.134 16.134s7.223 16.134 16.134 16.134h252.773V223.73c0 8.912 7.223 16.134 16.134 16.134h125.478l23.497 30.268v83.211h-44.639c-8.912 0-16.134 7.223-16.134 16.134 0 8.912 7.223 16.134 16.134 16.134h60.773c8.912 0 16.134-7.223 16.135-16.134V264.605c0-3.582-1.194-7.067-3.388-9.896zM116.706 271.597H42.487c-8.912 0-16.134 7.223-16.134 16.134 0 8.912 7.223 16.134 16.134 16.134h74.218c8.912 0 16.134-7.223 16.134-16.134.001-8.911-7.222-16.134-16.133-16.134zM153.815 208.134H16.134C7.223 208.134 0 215.357 0 224.269s7.223 16.134 16.134 16.134h137.681c8.912 0 16.134-7.223 16.134-16.134s-7.222-16.135-16.134-16.135z"
									fill="#31c48d"
									opacity="1"
									data-original="#000000"
								></path>
								<path
									d="M180.168 144.672H42.487c-8.912 0-16.134 7.223-16.134 16.134 0 8.912 7.223 16.134 16.134 16.134h137.681c8.912 0 16.134-7.223 16.134-16.134.001-8.911-7.222-16.134-16.134-16.134z"
									fill="#31c48d"
									opacity="1"
									data-original="#000000"
								></path>
							</g>
						</svg>
						<span className={`${loading ? 'opacity-30' : 'opacity-100'} ms-1`} >

							Download Invoice
						</span>

						{/* Spinner overlays the text */}
						{loading && (
							<span className="absolute inset-0 flex items-center justify-center">
								<svg
									className="animate-spin h-5 w-5 text-green-400"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									></circle>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
									></path>
								</svg>
							</span>
						)}
					</button>

				</div>
			</div>

			<div className="flex flex-col gap-4 lg:flex-row">
				<div className="space-y-4 lg:w-1/3">
					<div className="rounded-lg bg-gray-100 p-4">
						<h4 className="mb-2 text-lg font-semibold">Customer information</h4>
						<p className="text-sm font-medium">{order?.user?.full_name}</p>
						<a href="#" className="text-sm font-medium text-purple-900">
							{order?.user?.email}
						</a>
						<p></p>
						<a href="#" className="text-sm font-medium text-purple-900">
							{order?.user?.phone_number}
						</a>
					</div>
					<div className="rounded-lg bg-gray-100 p-4">
						<h4 className="mb-2 text-lg font-semibold">Shipping address</h4>
						{order?.shipping?.address1 ||
							order?.shipping?.address2 ||
							order?.shipping?.city ||
							order?.shipping?.country ? (
							<p className="text-sm font-medium">
								{order?.shipping?.address1 ? `${order.shipping.address1}, ` : ''}
								{order?.shipping?.address2 ? `${order.shipping.address2}, ` : ''}
								{order?.shipping?.city ? `${order.shipping.city}, ` : ''}
								{order?.shipping?.country || ''}
							</p>
						) : (
							<p className="text-sm font-medium text-gray-400">Shipping address not available</p>
						)}

						<p className="mt-2 text-sm font-medium text-purple-900">{order?.shipping?.phone}</p>
					</div>
				</div>

				<div className="mb-6 w-full rounded-lg border border-gray-200 bg-white shadow">
					<table className="mb-4 w-full text-left">
						<thead className="bg-gray-100">
							<tr className="text-left text-sm font-medium text-gray-900">
								<th className="p-2 font-semibold">Product</th>
								<th className="p-2 text-center font-semibold">Price</th>
								<th className="p-2 text-center font-semibold">Quantity</th>
								<th className="p-2 text-end font-semibold">Subtotal</th>
							</tr>
						</thead>
						<tbody>
							{order?.order_items?.map((item: _object, i: number) => {
								return (
									<tr className="border-b" key={i}>
										<td className="flex items-center gap-2 p-2">
											<img src={item?.product?.image || Placeholder} className="rounded" height={40} width={40} />
											<span>{item?.product?.new_title ? item?.product?.new_title.toUpperCase() : item?.product?.title}</span>
										</td>
										<td className="p-2 text-center">{formatPrice(item?.price)}</td>
										<td className="p-2 text-center">{item?.quantity}</td>
										<td className="p-2 text-end">{formatPrice(item?.subtotal)}</td>
									</tr>
								)
							})}
							{order?.subtotal &&
								<tr>
									<td colSpan={3} className="p-2 pt-2 text-end font-bold">
										Subtotal
									</td>
									<td className="p-2 pt-2 text-end"> {formatPrice(order?.subtotal)}</td>
								</tr>
							}
							{order?.discount_total && (
								<tr>
									<td colSpan={3} className="p-2 pt-2 text-end font-bold">
										Discount
									</td>
									<td className="p-2 pt-2 text-end"> {formatPrice(order?.discount_total)}</td>
								</tr>
							)}

							{order?.tax_total ? (
								<tr>
									<td colSpan={3} className="p-2 pt-2 text-end font-bold">
										Tax
									</td>
									<td className="p-2 pt-2 text-end"> {formatPrice(order?.tax_total) || ''}</td>
								</tr>
							)
								:
								''
							}
							{order?.total &&
								<tr>
									<td colSpan={3} className="p-2 pt-2 text-end font-bold">
										Total
									</td>
									<td className="p-2 pt-2 text-end"> {formatPrice(order?.total)}</td>
								</tr>
							}
						</tbody>
					</table>
				</div>

				<div className="rounded-lg bg-gray-100 p-4 lg:w-1/3">
					<div>
						<h4 className="mb-4 text-lg font-semibold">Payment information</h4>
						{order?.payment_term &&
							<div className="flex items-center gap-1 mb-2">
								<p className="text-sm font-bold">Term:</p>
								<span className="text-sm font-medium">{formatPaymentType(order?.payment_term)}</span>
							</div>
						}
						{order?.payment_due_date &&
							<div className="flex items-center gap-1 mb-2">
								<p className="text-sm font-bold whitespace-nowrap">Due Date:</p>
								<span className="text-sm font-medium whitespace-nowrap"> {dateFormatWitTime(order?.payment_due_date)}</span>

							</div>
						}
					</div>
				</div>
			</div>
		</>
	)
}
export default OrderDetails
